import React from 'react';

const fmtN = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });

/**
 * Trả về package nhập hàng mặc định (không phải base unit).
 * Ưu tiên: is_default=true → bất kỳ non-base → null (nếu chỉ có base unit).
 */
export function getDefaultPackage(material, packageOverride) {
  if (packageOverride && !packageOverride.is_base_unit) return packageOverride;
  const pkgs = material?.packages;
  if (!pkgs?.length) return null;
  const nonBase = pkgs.filter((p) => !p.is_base_unit);
  if (!nonBase.length) return null;
  return nonBase.find((p) => p.is_default) ?? nonBase[0];
}

/**
 * Chọn quy cách MUA của 1 NVL để điền sẵn vào form (NCC, phiếu đặt hàng).
 *
 * Khác `getDefaultPackage`: hàm này luôn trả về 1 quy cách nếu có (fallback về đơn vị cơ sở),
 * vì form bắt buộc phải chọn gì đó.
 *
 * Đơn vị cơ sở (1g / 1 cái) do backend tự tạo và thường mang is_default=true, nên nếu chỉ dựa vào
 * is_default thì form sẽ mặc định "1g" thay vì "1 bì 500g" (ON-118).
 *
 * @param preferred quy cách ưu tiên (VD quy cách NCC đã cấu hình) — bỏ qua nếu không còn active
 */
export function pickPurchasePackage(packages, preferred) {
  const pkgs = (packages ?? []).filter((p) => p.is_active !== false);
  if (preferred && pkgs.some((p) => p.id === preferred.id)) return preferred;
  if (!pkgs.length) return null;

  const nonBase = pkgs.filter((p) => !p.is_base_unit);
  if (nonBase.length) return nonBase.find((p) => p.is_default) ?? nonBase[0];

  return pkgs.find((p) => p.is_default) ?? pkgs[0];
}

/**
 * Nhãn quy cách kèm hệ số quy đổi: "Bì (500 g)".
 * Không có hệ số thì chọn nhầm đơn vị cơ sở 1g là hoàn toàn vô hình (ON-118).
 */
export function packageOptionLabel(pkg, materialUnit) {
  if (!pkg) return '';
  const label = pkg.unit_label ?? pkg.name ?? '';
  const qib = Number(pkg.quantity_in_base ?? 0);
  if (!qib) return label;
  return `${label} (${fmtN(qib)} ${materialUnit ?? ''})`.trim();
}

/**
 * Format số lượng theo dual unit.
 * Trả về { primary, secondary } — secondary là null nếu không có package nhập.
 */
export function formatQtyDual(quantityInBase, material, packageOverride) {
  const pkg = getDefaultPackage(material, packageOverride);
  const qib = Number(pkg?.quantity_in_base ?? 0);

  if (!pkg || qib <= 0) {
    return { primary: `${fmtN(quantityInBase)} ${material?.unit ?? ''}`, secondary: null };
  }

  const pkgLabel = pkg.unit_label ?? pkg.name ?? '';
  const pkgQty = Number(quantityInBase ?? 0) / qib;
  return {
    primary: `${fmtN(pkgQty)} ${pkgLabel}`,
    secondary: `= ${fmtN(quantityInBase)} ${material?.unit ?? ''}`,
  };
}

/**
 * #232210: quy đổi 2 CHIỀU giữa số nhập theo quy cách đóng gói và base-unit lưu CSDL, dùng cho
 * ô nhập "ngưỡng tối thiểu" (thay vì bắt nhập thẳng gam/ml). Không có package nào ngoài base unit
 * thì coi số nhập/hiển thị THẲNG là base-unit (đúng fallback của `formatQtyDual`).
 */
export function baseToPackageQty(quantityInBase, material, packageOverride) {
  const pkg = getDefaultPackage(material, packageOverride);
  const qib = Number(pkg?.quantity_in_base ?? 0);
  if (!pkg || qib <= 0) return Number(quantityInBase ?? 0);
  return Number(quantityInBase ?? 0) / qib;
}

export function packageQtyToBase(packageQty, material, packageOverride) {
  const pkg = getDefaultPackage(material, packageOverride);
  const qib = Number(pkg?.quantity_in_base ?? 0);
  if (!pkg || qib <= 0) return Number(packageQty ?? 0);
  return Number(packageQty ?? 0) * qib;
}

/**
 * Nhãn quy cách dùng cho ô nhập (không kèm hệ số quy đổi, khác `packageOptionLabel`) — fallback về
 * đơn vị cơ sở của NVL khi không có quy cách đóng gói nào (đếm rời).
 */
export function inputUnitLabel(material, packageOverride) {
  const pkg = getDefaultPackage(material, packageOverride);
  if (!pkg) return material?.unit ?? '';
  return pkg.unit_label ?? pkg.name ?? material?.unit ?? '';
}

/**
 * Component hiển thị số lượng 2 dòng: đơn vị nhập (lớn) + đơn vị cơ sở (nhỏ, muted).
 * Nếu NVL không có package nhập thì chỉ 1 dòng.
 */
export default function QtyDual({ quantityInBase, material, packageOverride, className = '' }) {
  const { primary, secondary } = formatQtyDual(quantityInBase, material, packageOverride);
  return (
    <span className={className}>
      {primary}
      {secondary && (
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{secondary}</div>
      )}
    </span>
  );
}

/**
 * #144941: hiển thị lại ĐÚNG số NV đã bấm ("còn nguyên" + "dở dang"), thay vì quy đổi liên tục
 * như formatQtyDual — mirror `fmtQtyByPackage` phía server (inventory-unit.util.ts).
 * `item` cần { unit, package_id, package_quantity, partial_quantity, partial_input_mode,
 * actual_quantity }, `packages` là danh sách MaterialPackage của đúng NVL đó.
 * Ví dụ: "3 gói + 60% dở", hoặc "2 gói" khi dở = 0%.
 */
export function formatQtyByPackage(item, packages) {
  const pkg = item?.package_id
    ? (packages ?? []).find((p) => p.id === item.package_id)
    : null;

  if (!pkg) {
    const qty = item?.package_quantity ?? item?.actual_quantity ?? 0;
    return `${fmtN(qty)} ${item?.unit ?? ''}`;
  }

  const whole = Number(item.package_quantity ?? 0);
  const pkgLabel = pkg.unit_label ?? pkg.name ?? '';
  const base = `${fmtN(whole)} ${pkgLabel}`;

  const partialRaw = Number(item.partial_quantity ?? 0);
  if (partialRaw <= 0) return base;

  const qib = Number(pkg.quantity_in_base ?? 0);
  const partialPct =
    item.partial_input_mode === 'FRACTION'
      ? Math.round(partialRaw * 100)
      : qib > 0
        ? Math.round((partialRaw / qib) * 100)
        : 0;
  if (partialPct <= 0) return base;

  return `${base} + ${partialPct}% dở`;
}
